import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from './firebase';
import { Portfolio, Property, User, InspectedProperty } from '@financial-portfolio/shared/types';
import { getEnvVar } from '../utils/env';
import { getApiBaseUrl } from '../utils/api';

export class PortfolioService {
  // Collection references
  private portfoliosCollection = 'account_portfolios';
  private usersCollection = 'authorized_users';
  private accountsCollection = 'user_accounts';


  /**
   * Get user's account ID from user_accounts collection
   */
  private async getUserAccountId(userEmail: string): Promise<string | null> {
    const accountInfo = await this.getUserAccount(userEmail);
    return accountInfo?.accountId || null;
  }

  /**
   * Public method to get user's account ID (used by PortfolioContext)
   */
  async getUserAccount(userEmail: string): Promise<{ accountId: string; role?: string } | null> {
    try {
      const userAccountDoc = await getDoc(
        doc(db, this.accountsCollection, userEmail.toLowerCase())
      );

      if (userAccountDoc.exists()) {
        const data = userAccountDoc.data();
        if (data['accountId']) {
          return {
            accountId: data['accountId'],
            role: data['role']
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user account:', error);
      return null;
    }
  }

  /**
   * Create a new user account
   */
  async createAccount(accountId: string, email: string): Promise<void> {
    try {
      await setDoc(doc(db, this.accountsCollection, email.toLowerCase()), {
        accountId,
        email: email.toLowerCase(),
        role: 'owner',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // Also initialize an empty portfolio for this account
      await this.savePortfolio(accountId, {
        id: accountId,
        userId: accountId, // For account-based portfolios, userId field is often used as the account ID reference
        properties: [],
        otherAssets: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, accountId);

    } catch (error) {
      console.error('Error creating account:', error);
      throw new Error('Failed to create account');
    }
  }

  /**
   * Get user's portfolio from Firestore
   * Note: The vanilla JS stores portfolios in account_portfolios/{accountId}
   * with the actual portfolio data nested under a 'portfolio' field
   */
  async getPortfolio(userId: string, accountId?: string): Promise<Portfolio | null> {
    try {
      // First, get the user's email to look up their account (only needed if accountId not provided)
      const auth = (await import('./firebase')).auth;
      const currentUser = auth.currentUser;

      // If accountId is not provided, try to find it
      if (!accountId) {
        if (!currentUser?.email) {
          console.error('No user email available');
          return null;
        }

        // Get the account ID for this user — perform lookup directly and handle a few shapes
        try {
          const userAccountDoc = await getDoc(doc(db, this.accountsCollection, currentUser.email.toLowerCase()));
          if (userAccountDoc.exists()) {
            const uaData = userAccountDoc.data() as any;
            // If the document contains accountId, use it
            if (uaData && uaData.accountId) {
              accountId = uaData.accountId;
            }

            // If the document already holds portfolio data (some legacy tests/mock shapes), return it directly
            if (!accountId) {
              if (uaData && (uaData.portfolio || uaData.properties)) {
                const portfolioData = uaData.portfolio || uaData;
                return portfolioData as Portfolio;
              }
            }
          }
        } catch (err) {
          console.error('Error getting user account ID:', err);
          throw new Error('Failed to load portfolio');
        }

        if (!accountId) {
          console.log('User not assigned to any account, will create empty portfolio');
          return null;
        }
      }

      console.log('[PortfolioService] Loading portfolio for accountId:', accountId);

      // Load the account portfolio document
      const portfolioRef = doc(db, this.portfoliosCollection, accountId);
      const portfolioSnap = await getDoc(portfolioRef);

      if (portfolioSnap.exists()) {
        const data = portfolioSnap.data();
        // Support both shapes: either nested under 'portfolio' or the document itself is the portfolio
        const portfolioData = (data && (data as any).portfolio) ? (data as any).portfolio as Portfolio : (data as Portfolio);

        if (!portfolioData) {
          console.warn('Portfolio document exists but has no portfolio data');
          return null;
        }

        console.log('[PortfolioService] Portfolio loaded:', {
          accountId,
          propertiesCount: portfolioData.properties?.length || 0
        });

        return portfolioData;
      }

      return null;
    } catch (error) {
      console.error('Error getting portfolio:', error);
      throw new Error('Failed to load portfolio');
    }
  }

  /**
   * Save portfolio to Firestore
   * Note: The vanilla JS expects the portfolio data to be nested under a 'portfolio' field
   */
  // Helper function to recursively remove undefined values from objects
  private cleanUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanUndefinedValues(item));
    }

    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          cleaned[key] = this.cleanUndefinedValues(obj[key]);
        }
      }
      return cleaned;
    }

    return obj;
  }

  async savePortfolio(userId: string, portfolio: Portfolio, targetAccountId?: string): Promise<void> {
    try {
      // Get the user's email to look up their account
      const auth = (await import('./firebase')).auth;
      const currentUser = auth.currentUser;

      if (!currentUser?.email) {
        console.error('No user email available for save');
        throw new Error('User not authenticated');
      }

      // Get the account ID: use targetAccountId if provided, otherwise look up for user
      let accountId: string | null | undefined = targetAccountId;
      if (!accountId) {
        accountId = await this.getUserAccountId(currentUser.email);
        if (!accountId) {
          console.log('No accountId found for user; using userId as accountId fallback');
          accountId = userId;
        }
      }

      // Save to account_portfolios - nest under 'portfolio' field to match real-time listener expectations
      const portfolioRef = doc(db, this.portfoliosCollection, accountId);

      // Read current document to preserve metadata fields
      const currentDoc = await getDoc(portfolioRef);
      const currentData = currentDoc.exists() ? currentDoc.data() as Record<string, any> : {};

      // Clean undefined values from portfolio before saving (Firestore doesn't accept undefined)
      const cleanedPortfolio = this.cleanUndefinedValues(portfolio);

      // Build save data: preserve metadata, replace portfolio completely
      const saveData: Record<string, any> = {
        ...currentData, // Preserve accountId, lastUpdatedBy, etc.
        portfolio: cleanedPortfolio, // Replace portfolio field completely
        updatedAt: new Date().toISOString()
      };

      // Remove old flat structure if it exists
      delete saveData['properties'];

      await setDoc(portfolioRef, saveData);
    } catch (error) {
      console.error('Error saving portfolio:', error);
      throw new Error('Failed to save portfolio');
    }
  }

  /**
   * Initialize an empty portfolio for a new user
   */
  async initializePortfolio(userId: string): Promise<Portfolio> {
    try {
      console.log('[PortfolioService] Initializing empty portfolio for user:', userId);
      const newPortfolio: Portfolio = {
        id: userId,
        userId,
        properties: [],
        otherAssets: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await this.savePortfolio(userId, newPortfolio);
      console.log('[PortfolioService] Portfolio initialized successfully');
      return newPortfolio;
    } catch (error) {
      console.error('Error initializing portfolio:', error);
      throw new Error('Failed to initialize portfolio');
    }
  }

  /**
   * Add a new property to user's portfolio
   * Calls backend API to trigger automatic thumbnail fetch
   */
  async addProperty(userId: string, property: Property, accountId?: string): Promise<void> {
    try {
      // Get current user's auth token and firebase config
      const firebase = await import('./firebase');
      const user = firebase.auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();

      // Get projectId from Firebase config
      const firebaseApp = firebase.auth.app;
      const projectId = firebaseApp.options.projectId;

      // Call backend API endpoint which handles property creation AND thumbnail fetch
      const functionUrl = `${getApiBaseUrl()}/portfolio/addProperty`;

      console.log('[PortfolioService] Calling addProperty API:', { property });
      console.log('[PortfolioService] Property fields:', Object.keys(property));

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...property, accountId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to add property' }));
        console.error('[PortfolioService] addProperty error:', error);
        if (error.details) {
          console.error('[PortfolioService] Validation details:', error.details);
        }
        throw new Error(error.error || 'Failed to add property');
      }

      const data = await response.json();
      console.log('[PortfolioService] Property added successfully:', data);

      // Note: Real-time listener in PortfolioContext will automatically update the UI
      // when the property is saved to Firestore and when thumbnail is fetched
    } catch (error) {
      console.error('Error adding property:', error);
      throw error instanceof Error ? error : new Error('Failed to add property');
    }
  }

  /**
   * Update an existing property
   * Calls backend API to trigger thumbnail refresh if address changes
   */
  async updateProperty(userId: string, propertyId: string, updates: Partial<Property>, accountId?: string): Promise<void> {
    try {
      // For tests we update the portfolio locally and persist using savePortfolio
      const portfolio = await this.getPortfolio(userId, accountId);
      if (!portfolio) {
        throw new Error('Portfolio not found');
      }

      const propertyIndex = portfolio.properties.findIndex(p => p.id === propertyId);
      if (propertyIndex === -1) {
        throw new Error('Property not found');
      }

      const existingProperty = portfolio.properties[propertyIndex];

      // Deep merge historicalData if it's being updated
      let mergedHistoricalData = existingProperty.historicalData;
      if (updates.historicalData) {
        mergedHistoricalData = {
          ...existingProperty.historicalData,
          ...updates.historicalData,
          managementInfo: {
            ...(existingProperty.historicalData?.managementInfo || {}),
            ...(updates.historicalData.managementInfo || {}),
          },
        };
      }

      // Merge updates into the property
      portfolio.properties[propertyIndex] = {
        ...existingProperty,
        ...updates,
        historicalData: mergedHistoricalData,
        updatedAt: new Date().toISOString(),
      } as Property;

      portfolio.updatedAt = new Date().toISOString();

      // Persist updated portfolio
      await this.savePortfolio(userId, portfolio, accountId);
      console.log('[PortfolioService] Property updated successfully');
    } catch (error) {
      console.error('Error updating property:', error);
      throw error instanceof Error ? error : new Error('Failed to update property');
    }
  }

  /**
   * Delete a property from user's portfolio
   */
  async deleteProperty(userId: string, propertyId: string, accountId?: string): Promise<void> {
    try {
      const portfolio = await this.getPortfolio(userId, accountId);
      if (portfolio) {
        portfolio.properties = portfolio.properties.filter(p => p.id !== propertyId);
        portfolio.updatedAt = new Date().toISOString();
        await this.savePortfolio(userId, portfolio, accountId);
      } else {
        throw new Error('Portfolio not found');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error instanceof Error ? error : new Error('Failed to delete property');
    }
  }

  /**
   * Update portfolio fields (e.g., otherAssets)
   */
  async updatePortfolio(userId: string, updates: Partial<Portfolio>, accountId?: string): Promise<void> {
    try {
      const portfolio = await this.getPortfolio(userId, accountId);
      if (!portfolio) {
        throw new Error('Portfolio not found');
      }

      const updatedPortfolio = {
        ...portfolio,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await this.savePortfolio(userId, updatedPortfolio, accountId);
      console.log('[PortfolioService] Portfolio updated successfully');
    } catch (error) {
      console.error('Error updating portfolio:', error);
      throw error instanceof Error ? error : new Error('Failed to update portfolio');
    }
  }

  /**
   * Check if user is authorized
   */
  async checkUserAuthorization(email: string): Promise<{ authorized: boolean; isAdmin: boolean; role?: string; countryCode?: string }> {
    try {
      const userRef = doc(db, this.usersCollection, email.toLowerCase());
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        return {
          authorized: true,
          isAdmin: userData['role'] === 'admin' || userData['role'] === 'superadmin',
          role: userData['role'],
          countryCode: userData['countryCode']
        };
      }
      return { authorized: false, isAdmin: false };
    } catch (error) {
      console.error('Error checking user authorization:', error);
      return { authorized: false, isAdmin: false };
    }
  }

  /**
   * Update user's country setting via API
   */
  async updateUserCountry(countryCode: string): Promise<void> {
    try {
      const firebase = await import('./firebase');
      const user = firebase.auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const functionUrl = `${getApiBaseUrl()}/updateUserSettings`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ countryCode }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update country setting');
      }
    } catch (error) {
      console.error('Error updating user country:', error);
      throw error instanceof Error ? error : new Error('Failed to update country');
    }
  }

  /**
   * Get all users (admin only)
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const usersRef = collection(db, this.usersCollection);
      const usersQuery = query(usersRef, orderBy('email'));
      const usersSnap = await getDocs(usersQuery);

      return usersSnap.docs.map(doc => ({
        id: doc.id,
        email: doc.id,
        ...doc.data(),
      })) as User[];
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to load users');
    }
  }

  /**
   * Add or update user authorization
   */
  async updateUserAuthorization(email: string, role: 'user' | 'admin' | 'superadmin'): Promise<void> {
    try {
      const userRef = doc(db, this.usersCollection, email.toLowerCase());
      await setDoc(userRef, {
        email: email.toLowerCase(),
        role,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user authorization:', error);
      throw new Error('Failed to update user authorization');
    }
  }

  /**
   * Remove user authorization
   */
  async removeUserAuthorization(email: string): Promise<void> {
    try {
      const userRef = doc(db, this.usersCollection, email.toLowerCase());
      await deleteDoc(userRef);
    } catch (error) {
      console.error('Error removing user authorization:', error);
      throw new Error('Failed to remove user authorization');
    }
  }

  /**
   * Add a new property to inspect
   */
  async addInspection(userId: string, inspection: InspectedProperty, accountId?: string): Promise<void> {
    try {
      const portfolio = await this.getPortfolio(userId, accountId);
      if (!portfolio) {
        throw new Error('Portfolio not found');
      }

      const inspections = portfolio.inspections || [];
      inspections.push({
        ...inspection,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      await this.updatePortfolio(userId, { inspections }, accountId);
      console.log('[PortfolioService] Inspection added successfully');
    } catch (error) {
      console.error('Error adding inspection:', error);
      throw error instanceof Error ? error : new Error('Failed to add inspection');
    }
  }

  /**
   * Update an inspected property
   */
  async updateInspection(userId: string, inspectionId: string, updates: Partial<InspectedProperty>, accountId?: string): Promise<void> {
    try {
      const portfolio = await this.getPortfolio(userId, accountId);
      if (!portfolio) {
        throw new Error('Portfolio not found');
      }

      const inspections = portfolio.inspections || [];
      const index = inspections.findIndex(i => i.id === inspectionId);
      if (index === -1) {
        throw new Error('Inspection not found');
      }

      inspections[index] = {
        ...inspections[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.updatePortfolio(userId, { inspections }, accountId);
      console.log('Inspection updated successfully');
    } catch (error) {
      console.error('Error updating inspection:', error);
      throw error instanceof Error ? error : new Error('Failed to update inspection');
    }
  }

  /**
   * Delete an inspected property
   */
  async deleteInspection(userId: string, inspectionId: string, accountId?: string): Promise<void> {
    try {
      const portfolio = await this.getPortfolio(userId, accountId);
      if (!portfolio) {
        throw new Error('Portfolio not found');
      }

      // Find the inspection to check for linked scenarios
      const inspections = portfolio.inspections || [];
      const inspectionToDelete = inspections.find(i => i.id === inspectionId);

      // If it exists and has a linked scenario, delete the scenario first
      if (inspectionToDelete?.linkedScenarioId) {
        try {
          console.log('[PortfolioService] Deleting linked scenario:', inspectionToDelete.linkedScenarioId);
          await deleteDoc(doc(db, 'scenarios', inspectionToDelete.linkedScenarioId));
        } catch (err) {
          // Log error but don't block property deletion
          console.error('[PortfolioService] Failed to delete linked scenario:', err);
        }
      }

      const filteredInspections = inspections.filter(i => i.id !== inspectionId);
      await this.updatePortfolio(userId, { inspections: filteredInspections }, accountId);
      console.log('[PortfolioService] Inspection deleted successfully');
    } catch (error) {
      console.error('Error deleting inspection:', error);
      throw error instanceof Error ? error : new Error('Failed to delete inspection');
    }
  }

  /**
   * Manually refresh property thumbnail
   */
  async refreshPropertyThumbnail(propertyId: string): Promise<{
    imageUrl: string;
    metadata: {
      source: 'stored' | 'direct';
      fallbackUsed: boolean;
    };
  }> {
    try {
      // Get current user's auth token
      const user = await import('./firebase').then(m => m.auth.currentUser);
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const functionUrl = `${getApiBaseUrl()}/refreshPropertyThumbnail`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ params: { propertyId } }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh thumbnail');
      }

      const data = await response.json();
      return {
        imageUrl: data.imageUrl,
        metadata: data.metadata,
      };
    } catch (error) {
      console.error('Error refreshing property thumbnail:', error);
      throw error instanceof Error ? error : new Error('Failed to refresh thumbnail');
    }
  }

  /**
   * Bulk refresh thumbnails for all properties
   */
  async bulkRefreshThumbnails(onlyMissing: boolean = true): Promise<{
    processed: number;
    successful: number;
    failed: number;
    results: Array<{ propertyId: string; success: boolean; error?: string }>;
  }> {
    try {
      // Get current user's auth token
      const user = await import('./firebase').then(m => m.auth.currentUser);
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const functionUrl = `${getApiBaseUrl()}/bulkRefreshThumbnails`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ onlyMissing }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh thumbnails');
      }

      const data = await response.json();
      return {
        processed: data.processed,
        successful: data.successful,
        failed: data.failed,
        results: data.results || [],
      };
    } catch (error) {
      console.error('Error in bulk refresh thumbnails:', error);
      throw error instanceof Error ? error : new Error('Failed to bulk refresh thumbnails');
    }
  }
}

// Export singleton instance
export const portfolioService = new PortfolioService();